import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, TrendingUp, CheckCircle, XCircle, Activity, Zap, Settings, Bell, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AIDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: true,
    threshold_count: 5,
    threshold_hours: 1,
    actor_specific: false,
    notification_type: "badge"
  });
  const [showSettings, setShowSettings] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchDashboardStats();
    fetchNotifications();
    fetchNotificationSettings();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/ai-dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard stats", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/notifications/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/notifications/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch notification settings", error);
    }
  };

  const updateNotificationSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/admin/notifications/settings`, notificationSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Settings Updated",
        description: "Notification settings have been saved successfully."
      });
      setShowSettings(false);
      fetchNotifications(); // Refresh notifications with new settings
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update notification settings.",
        variant: "destructive"
      });
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/admin/notifications/${notificationId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.filter(n => n.id !== notificationId));
      toast({
        title: "Dismissed",
        description: "Notification has been dismissed."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to dismiss notification.",
        variant: "destructive"
      });
    }
  };

  const dismissAllNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/notifications/dismiss-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications([]);
      toast({
        title: "All Dismissed",
        description: "All notifications have been dismissed."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to dismiss notifications.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Loading dashboard...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">AI Dashboard</h1>
          <p className="text-muted-foreground">Monitor AI model performance and errors</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowSettings(!showSettings)}
          className="gap-2"
        >
          <Settings className="w-4 h-4" />
          Notification Settings
        </Button>
      </div>

      {/* Notification Settings Panel */}
      {showSettings && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Notification Settings
            </CardTitle>
            <CardDescription>
              Configure when you want to be notified about model failures
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Notifications</Label>
                <p className="text-sm text-muted-foreground">Receive alerts when error thresholds are exceeded</p>
              </div>
              <Switch
                checked={notificationSettings.enabled}
                onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, enabled: checked})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="threshold_count">Error Count Threshold</Label>
                <Input
                  id="threshold_count"
                  type="number"
                  min="1"
                  value={notificationSettings.threshold_count}
                  onChange={(e) => setNotificationSettings({...notificationSettings, threshold_count: parseInt(e.target.value)})}
                />
                <p className="text-xs text-muted-foreground">Number of errors to trigger alert</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="threshold_hours">Time Window (Hours)</Label>
                <Input
                  id="threshold_hours"
                  type="number"
                  min="1"
                  max="24"
                  value={notificationSettings.threshold_hours}
                  onChange={(e) => setNotificationSettings({...notificationSettings, threshold_hours: parseInt(e.target.value)})}
                />
                <p className="text-xs text-muted-foreground">Within this many hours</p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Per-Actor Threshold</Label>
                <p className="text-sm text-muted-foreground">Apply threshold to each AI Actor individually</p>
              </div>
              <Switch
                checked={notificationSettings.actor_specific}
                onCheckedChange={(checked) => setNotificationSettings({...notificationSettings, actor_specific: checked})}
              />
            </div>

            <div className="flex items-center gap-2 pt-4">
              <Button onClick={updateNotificationSettings}>Save Settings</Button>
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Notifications */}
      {notifications.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-amber-900">
                <Bell className="w-5 h-5" />
                Active Alerts ({notifications.length})
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissAllNotifications}
                className="text-amber-700 hover:text-amber-900"
              >
                Dismiss All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="flex items-start justify-between p-3 bg-white rounded-lg border border-amber-200"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-amber-900">{notification.title}</p>
                    <Badge variant={notification.severity === "error" ? "destructive" : "default"}>
                      {notification.severity}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(notification.created_at).toLocaleString()}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => dismissNotification(notification.id)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_messages || 0}</div>
            <p className="text-xs text-muted-foreground">All time</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.success_rate ? `${stats.success_rate.toFixed(1)}%` : '0%'}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.successful_messages || 0} successful
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Errors</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.total_errors || 0}</div>
            <p className="text-xs text-muted-foreground">All models</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unresolved</CardTitle>
            <AlertTriangle className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.unresolved_errors || 0}</div>
            <p className="text-xs text-muted-foreground">Need attention</p>
          </CardContent>
        </Card>
      </div>

      {/* Most Failed Models */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-red-600" />
            Most Failed Models
          </CardTitle>
          <CardDescription>
            Models with highest error rates (both primary and backup failures)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stats.most_failed_models && stats.most_failed_models.length > 0 ? (
            <div className="space-y-3">
              {stats.most_failed_models.map((model, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                  <div className="flex-1">
                    <p className="font-semibold">{model.model}</p>
                    <p className="text-sm text-muted-foreground">
                      {model.error_count} {model.error_count === 1 ? 'error' : 'errors'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">{model.error_count}</Badge>
                    <div className="w-32 bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-red-600 h-2 rounded-full" 
                        style={{ 
                          width: `${Math.min((model.error_count / (stats.total_errors || 1)) * 100, 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
              <p className="text-muted-foreground">No model failures! 🎉</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Types Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Error Types
          </CardTitle>
          <CardDescription>
            Breakdown of error categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stats.error_types && stats.error_types.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stats.error_types.map((type, index) => (
                <div key={index} className="p-4 border rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{type.count}</p>
                  <p className="text-sm font-medium text-muted-foreground capitalize">{type.type}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {((type.count / (stats.total_errors || 1)) * 100).toFixed(1)}% of errors
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No errors to display</p>
          )}
        </CardContent>
      </Card>

      {/* Most Affected AI Actors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-indigo-600" />
            Most Affected AI Actors
          </CardTitle>
          <CardDescription>
            AI Actors experiencing the most errors
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stats.most_affected_actors && stats.most_affected_actors.length > 0 ? (
            <div className="space-y-2">
              {stats.most_affected_actors.map((actor, index) => (
                <div key={index} className="flex items-center justify-between p-2 border rounded">
                  <span className="font-medium">{actor.actor_name}</span>
                  <Badge variant="outline">{actor.error_count} errors</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No actor-specific errors</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Errors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            Recent Errors (Last 24 Hours)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recent_errors && stats.recent_errors.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_errors.slice(0, 5).map((error, index) => (
                <div key={index} className="flex items-start justify-between p-3 border rounded-lg bg-red-50">
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{error.actor_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {error.primary_model} → {error.backup_model || 'No backup'}
                    </p>
                    <Badge variant="outline" className="text-xs mt-1">{error.error_type}</Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {new Date(error.timestamp).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
              <p className="text-muted-foreground">No errors in the last 24 hours! 🎉</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
